/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useRef } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setRoles } from "data/store/reducers/roles";
import { 
    setPermissions,
    setPermissionMappings,
    setPermissionsReady,
} from "data/store/reducers/permissions";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { Logger } from "cms/utils/log/logger";
import { isArray } from "lodash";
import { isArrayNotEmpty, isStringNotEmpty } from "utils/empty-exists";
// import { Logger } from "cms/utils/log/logger";

const PERMISSION = {
    ORGANIZATION: "permission.organization.view",
    SIMPLICITY_INTERNAL: "permission.simplicity.internal",
    COMPOSER_EDIT: "permission.composer.edit",
    COMPOSER: "permission.composer.view",
    ORGANIZATION_MANAGE: "permission.organization.manage",
    DATA: "permission.data.view",
    WEBSITE: "permission.website.view",
    COMPONENTS: "permission.components.view",
    TASKS: "permission.tasks.view",
    PUBLISHING: "permission.publishing.publish",
    ADD_TO_REVIEW_QUEUE: "permission.publishing.add-to-queue",
    PUBLISHING_ALERTS: "permission.publishing.alerts",
    PUBLISHING_REVIEW: "permission.publishing.review",
    USERS_MANAGE: "permission.users.manage",
    DIGITAL_ASSETS: "permission.digital-assets.view",
    FORMS: "permission.forms.view",
    ATTACHMENTS: "permission.forms.attachments",
    GENERAL: "permission.general.view",
    FORMS_PUBLISH: "permission.forms.publish",
    FORMS_EDIT: "permission.forms.edit",
    FORMS_INTEGRATION: "permission.forms.integration",
    MODEL_DESIGN: "permission.model.design",
    NOTIFICATION_ADMIN: "permission.notification.admin",
};

const PERMISSIONS = [
    { permission: PERMISSION.ORGANIZATION, label: "View Organization Section" },
    { permission: PERMISSION.SIMPLICITY_INTERNAL, label: "View Testing Section" },
    { permission: PERMISSION.COMPOSER_EDIT, label: "Edit Content" },
    { permission: PERMISSION.ORGANIZATION_MANAGE, label: "Edit Organization" },
    { permission: PERMISSION.DATA, label: "View Data Section" },
    { permission: PERMISSION.WEBSITE, label: "View Website Section" },
    { permission: PERMISSION.COMPONENTS, label: "View Components Section" },
    { permission: PERMISSION.TASKS, label: "View Tasks Section" },
    { permission: PERMISSION.PUBLISHING, label: "Publishing" },
    { permission: PERMISSION.ADD_TO_REVIEW_QUEUE, label: "Add to Review Queue" },
    { permission: PERMISSION.PUBLISHING_ALERTS, label: "Publishing Alerts" },
    { permission: PERMISSION.PUBLISHING_REVIEW, label: "Review" },
    { permission: PERMISSION.USERS_MANAGE, label: "Manage Users" },
    { permission: PERMISSION.COMPOSER, label: "View Composer Section" },
    { permission: PERMISSION.DIGITAL_ASSETS, label: "View Digital Assets Section" },
    { permission: PERMISSION.FORMS, label: "View Forms Section" },
    { permission: PERMISSION.ATTACHMENTS, label: "Access Form Attachments" },
    { permission: PERMISSION.GENERAL, label: "General Access" },
    { permission: PERMISSION.FORMS_PUBLISH, label: "Publish Forms" },
    { permission: PERMISSION.FORMS_EDIT, label: "Edit Forms" },
    { permission: PERMISSION.MODEL_DESIGN, label: "Design Models" },
];

const useRBAC = function () {

    const account_roles = useSelector((state) => state.account.roles);
    const roles = useSelector((state) => state.roles.all);
    const permissionsReady = useSelector((state) => state.permissions.ready);
    const permissions = useSelector((state) => state.permissions.all);
    const permission_roles = useSelector((state) => state.permissions.mappings);
    const dispatch = useDispatch();
    const { token } = useAuth();
    const loadingRef = useRef(false);
    
    const checkPermission = async (permissions) => {
        // accept 'permission' as either a string or array - populate 'permissions'
        if (isStringNotEmpty(permissions)) {
            permissions = [permissions];
        }
        if (!isArrayNotEmpty(permissions)) {
            Logger.warn("%c[checkPermission] error no permission requested", "color: red");
        }
        let hasPermission = false;
        if (isArray(permissions)) {
            for (const permission of permissions) {
                const permission_role = permission_roles.find((element) => {
                    return element.permission === permission;
                });
                const allowed_roles = permission_role?.roles ?? [];
                for (const roleStr of allowed_roles) {
                    const role = account_roles.find((acc_role) => {
                        return acc_role.type === roleStr;
                    });
                    if (role) {
                        hasPermission = true;
                    }
                }
            }
        } else {
            const msg = `[checkPermissionList] Permissions was not an array ${JSON.stringify(permissions)}`;
            Logger.warn(msg);
        }
        return hasPermission;
    };

    const fetchRoleData = async (token) => {
        const roles = await SimpliCityAdminSDK.role.find(token);
        const { results } = roles || {};
        dispatch(setRoles(results));
    };
    const fetchPermissionData = async (token) => {
        const permissions = await SimpliCityAdminSDK.permission.find(token);
        const { results } = permissions || {};
        dispatch(setPermissions(results));
    };
    const fetchPermissionRoleData = async (token) => {
        // TODO: rename SDK findRolesForPermission as "findPermissionRoleMappings" 
        //   input is token only.
        const mappings = await SimpliCityAdminSDK.permission.findRolesForPermission(token);
        dispatch(setPermissionMappings(mappings));
        // console.log(`%c[PermissionGuard] LOADING PERMISSIONS`, 'color: magenta', mappings)
    };

    const initializeRBAC = async () => {
        // console.log(`%c[use-RBAC] initialize()`, 'color: yellow')
        if (!loadingRef.current && roles?.length < 1) {
            loadingRef.current = true;
            await Promise.all([
                fetchRoleData(token),
                fetchPermissionData(token),
                fetchPermissionRoleData(token),
            ]).then(() => {
                dispatch(setPermissionsReady(true));
                loadingRef.current = false;
                // console.log(`%c[use-RBAC] permissions ready`, 'color: yellow')
            });
        }
    };
    
    return {
        initializeRBAC,
        account_roles,
        roles,
        permissions,
        permission_roles,
        PERMISSION,
        PERMISSIONS,
        checkPermission,
        permissionsReady,
    };
};

export { useRBAC, PERMISSION };
